/**
 * User Info Component
 *
 * @module modules/user/presentation/shared/components/UserInfo
 *
 * Displays user name and status information.
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { memo } from 'react';

import { useWorkTimeType } from '@/modules/workTime';

import type { UserInfoProps } from '../types';
import type { WorkTimeType } from '@/modules/workTime';

/**
 * Get status color based on work time status
 *
 * Supports work time status values:
 * - online: Green (available)
 * - working: Blue (actively working)
 * - pause: Orange (paused)
 * - offline: Gray (unavailable)
 */
function getStatusColor(status: WorkTimeType): string {
  switch (status) {
    case 'online':
      return 'success.main';
    case 'working':
      return 'primary.main';
    case 'paused':
      return 'warning.main';
    case 'offline':
    default:
      return 'text.disabled';
  }
}

/**
 * User Info Component
 *
 * Displays user name and secondary info (extension or email).
 * Includes a status indicator dot.
 *
 * @example
 * ```tsx
 * <UserInfo
 *   name="John Doe"
 *   email="john@example.com"
 *   extensionNumber="123"
 *   status="online"
 * />
 * ```
 */
export const UserInfo = memo(function UserInfo({ name, email, extensionNumber }: UserInfoProps) {
  const workTimeType = useWorkTimeType();
  const displayName = name || 'User';
  const secondaryInfo = extensionNumber ? `Ext. ${extensionNumber}` : email;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" fontWeight={600} noWrap component="div">
        {displayName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: getStatusColor(workTimeType),
          }}
        />
        <Typography variant="caption" color="text.secondary" fontSize="0.625rem" noWrap>
          {secondaryInfo}
        </Typography>
      </Box>
    </Box>
  );
});
